import * as React from 'react';
import { useTranslation } from 'react-i18next';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import Mic from '@mui/icons-material/Mic';
import MoneyOff from '@mui/icons-material/MoneyOff';
import Schedule from '@mui/icons-material/Schedule';
import Warning from '@mui/icons-material/Warning';
import YouTube from '@mui/icons-material/YouTube';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type { ITheme, Transcription } from '../../../..';
import { SheetSourceType, TranscriptionState } from '../../../..';

export type SongbookItemIconProps = { transcription: Transcription };

const useStyles = makeStyles()((theme: ITheme) => ({
  demoLabel: {
    fontWeight: 'bold',
    fontSize: '0.5rem',
    position: 'relative',
    color: '#ffffff',
    background: theme.palette.primary.main,
    padding: '0px 3px',
    borderRadius: '3px',
  },
  icon: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const SongbookItemIcon: React.FC<SongbookItemIconProps> = ({ transcription }) => {
  const { state, source } = transcription;
  const { t } = useTranslation();
  const { classes } = useStyles();

  const defaultColor = 'primary';
  let size = 20 * 1.5;
  const sizeProgressLabel = size / 2.5;

  let content: JSX.Element;
  let tooltip: string;

  switch (state) {
    case TranscriptionState.Ok:
      switch (source) {
        case SheetSourceType.File:
          tooltip = t('home.songbook.src_file');
          content = <InsertDriveFile color={defaultColor} />;
          break;
        case SheetSourceType.Microphone:
          tooltip = t('home.songbook.src_microphone');
          content = <Mic color={defaultColor} />;
          break;
        case SheetSourceType.YouTube:
          tooltip = t('home.songbook.src_youtube');
          content = <YouTube color={defaultColor} />;
          break;
        default:
          tooltip = t('home.songbook.state_ok');
      }
      break;
    case TranscriptionState.Enqueued:
      if (transcription.queuePosition >= 0) {
        tooltip = t('home.songbook.queue_position', { position: transcription.queuePosition });
      } else {
        tooltip = t('home.songbook.state_enqueued');
      }
      content = <Schedule color={defaultColor} />;
      break;
    case TranscriptionState.Processing:
      size = 16 * 2;
      tooltip = t('home.songbook.state_processing');
      content = (
        <Box sx={{ position: 'relative', display: 'inline-flex', marginBottom: '2px' }}>
          <CircularProgress size={30} color={defaultColor} variant="indeterminate" />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: sizeProgressLabel,
              marginBottom: '2px',
            }}
          >
            <span style={{ fontSize: sizeProgressLabel }}>
              {`${Math.round(transcription.progress)}%`}
            </span>
          </Box>
        </Box>
      );
      break;
    case TranscriptionState.Error:
      tooltip = t('home.songbook.state_error');
      content = <Warning color={defaultColor} />;
      break;
    case TranscriptionState.Refunded:
      tooltip = t('home.songbook.state_refunded');
      content = <MoneyOff color={defaultColor} />;
      break;
    default:
      tooltip = t('home.songbook.unknown');
      content = <InsertDriveFile color={defaultColor} />;
      break;
  }

  return (
    <Tooltip title={tooltip}>
      <div className={classes.icon}>
        {content}
        {transcription.demo && <span className={classes.demoLabel}>DEMO</span>}
      </div>
    </Tooltip>
  );
};

export default SongbookItemIcon;
